'use client';
import { useQuery } from '@tanstack/react-query';
import classnames from 'classnames';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { CartDto } from '@cineamo/api/models/cart/CartDto.types';
import { TicketDto } from '@cineamo/api/models/ticket/TicketDto';

import {
    downloadAppleWalletGroupTicket,
    downloadAppleWalletTicket,
    getGoogleWalletTicket,
    getGoogleWalletGroupTicket,
    downloadPdfTicket,
    downloadPdfTickets,
    getCartInvoice,
    getTickets,
    downloadPdfGroupTicket,
    getCartById
} from '@cineamo/api/resources/cart/cart-api';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { saveAs } from '@cineamo/ui/src/helpers/file-helper';
import { getAllListItems } from '@cineamo/ui/src/helpers/get-all-list-items';
import { getLanguageCodeFromString } from '@cineamo/ui/src/helpers/locale-helper';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { ButtonStyle } from '@cineamo/ui/src/button/ButtonStyle.enum';
import Button from '@cineamo/ui/src/button/NewButton';
import { IconDownload16 } from '@cineamo/ui/src/icons/ic-download';
import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconTick16 } from '@cineamo/ui/src/icons/ic-tick';
import ProgressBarAnimation from '@cineamo/ui/src/loading/ProgressBarAnimation';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';
const addToAppleWalletDE = loadAsset('/images/badges/im-addToAppleWallet-DE.svg');
const addToAppleWalletEN = loadAsset('/images/badges/im-addToAppleWallet-EN.svg');
const addToGoogleWalletDE = loadAsset('/images/badges/im-addToGoogleWallet-DE.svg');
const addToGoogleWalletEN = loadAsset('/images/badges/im-addToGoogleWallet-EN.svg');

export type TicketWithPass = {
    ticket: TicketDto;
    pkPass: File | null;
    googleWalletJwt: string | null;
};

type TicketRowProps = {
    title: string;
    cartUuid: CartDto['uuid'];
    cartTicketCount: number;
    ticketWithPass: TicketWithPass;
};

const TicketRow: React.FC<TicketRowProps> = ({ ticketWithPass, title, cartUuid, cartTicketCount }) => {
    const locale = useLocale();
    const isGerman = getLanguageCodeFromString(locale) === 'de';
    const { track } = useAnalytics();
    const t = useTranslations('tickets');

    const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);

    const AddToAppleWallet = (
        <Image
            width={153}
            height={48}
            className="w-[153px] h-[48px]"
            src={isGerman ? addToAppleWalletDE : addToAppleWalletEN}
            alt={t('add-to-apple-wallet')}
        />
    );
    const AddToGoogleWallet = (
        <Image
            width={153}
            height={48}
            className="w-[153px] h-[48px]"
            src={isGerman ? addToGoogleWalletDE : addToGoogleWalletEN}
            alt={t('add-to-google-wallet')}
        />
    );

    const onAppleWalletClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'Apple Wallet',
            type: 'single',
            cartTicketCount: cartTicketCount
        });
        saveAs(ticketWithPass?.pkPass ?? new Blob(), `${ticketWithPass?.ticket.ticketSystemId}.pkpass`);
    }, [ticketWithPass, track, cartTicketCount]);

    const onGoogleWalletClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'Google Wallet',
            type: 'single',
            cartTicketCount: cartTicketCount
        });
        window.open('https://pay.google.com/gp/v/save/' + ticketWithPass.googleWalletJwt, '_blank');
    }, [ticketWithPass, track, cartTicketCount]);

    const onPdfClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'PDF',
            type: 'single',
            cartTicketCount: cartTicketCount
        });
        setIsPdfLoading(true);
        downloadPdfTicket(cartUuid, ticketWithPass.ticket.id).then((result) => {
            if (result === null) {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('error.ticket-download-failed')
                });
            }
            setIsPdfLoading(false);
        });
    }, [cartUuid, ticketWithPass.ticket.id, track, cartTicketCount, t]);

    return (
        <div
            className={classnames(
                'rounded-6 flex flex-col lg:flex-row space-y-16 lg:space-y-0 lg:space-x-16 p-16',
                'bg-[linear-gradient(180deg,rgba(var(--surface-color-rgb),0.05)-0.14%,rgba(var(--surface-color-rgb),0.10)99.86%)]'
            )}>
            <div className="grow">
                <div className="text-body-bold-16">{title}</div>
                <div className="text-body-14">
                    {t('ticket-number', { ticketNumber: ticketWithPass.ticket.ticketSystemId ?? '' })}
                </div>
            </div>
            <div className="flex flex-row space-x-16 max-w-[100%] overflow-scroll">
                <Button
                    style={ButtonStyle.surface}
                    leadingIcon={isPdfLoading ? <IconLoading16 className="animate-spin" /> : <IconDownload16 />}
                    onClick={onPdfClick}>
                    PDF
                </Button>
                {ticketWithPass?.pkPass && (
                    <button
                        className="shrink-0"
                        onClick={onAppleWalletClick}>
                        {AddToAppleWallet}
                    </button>
                )}
                {ticketWithPass?.googleWalletJwt && (
                    <button
                        className="shrink-0"
                        onClick={onGoogleWalletClick}>
                        {AddToGoogleWallet}
                    </button>
                )}
            </div>
        </div>
    );
};

type GroupTicketRowProps = {
    cartUuid: CartDto['uuid'];
    cartTicketCount: number;
};
const GroupTicketRow: React.FC<GroupTicketRowProps> = ({ cartUuid, cartTicketCount }) => {
    const locale = useLocale();
    // See TicketRow above: the language subtag, not the whole locale.
    const isGerman = getLanguageCodeFromString(locale) === 'de';
    const { track } = useAnalytics();
    const t = useTranslations('tickets');

    const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);

    const { data: pkPass } = useQuery({
        queryKey: ['group-ticket-pkpass', cartUuid],
        queryFn: async () => {
            return await downloadAppleWalletGroupTicket(cartUuid);
        }
    });

    const { data: ticketId } = useQuery({
        queryKey: ['group-ticket-id', cartUuid],
        queryFn: async () => {
            const response = await getCartInvoice(cartUuid, 'json');
            return response?.ticketSystemId;
        }
    });

    const { data: googleWalletJwt } = useQuery({
        queryKey: ['group-ticket-google-wallet', cartUuid],
        queryFn: async () => {
            return await getGoogleWalletGroupTicket(cartUuid);
        }
    });

    const AddToAppleWallet = (
        <Image
            width={153}
            height={48}
            className="w-[153px] h-[48px]"
            src={isGerman ? addToAppleWalletDE : addToAppleWalletEN}
            alt={t('add-to-apple-wallet')}
        />
    );
    const AddToGoogleWallet = (
        <Image
            width={153}
            height={48}
            className="w-[153px] h-[48px]"
            src={isGerman ? addToGoogleWalletDE : addToGoogleWalletEN}
            alt={t('add-to-google-wallet')}
        />
    );

    const onAppleWalletClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'Apple Wallet',
            type: 'group',
            cartTicketCount: cartTicketCount
        });
        saveAs(pkPass ?? new Blob(), `${ticketId}.pkpasses`);
    }, [pkPass, track, ticketId, cartTicketCount]);

    const onGoogleWalletClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'Google Wallet',
            type: 'group',
            cartTicketCount: cartTicketCount
        });
        window.open('https://pay.google.com/gp/v/save/' + googleWalletJwt, '_blank');
    }, [googleWalletJwt, track, cartTicketCount]);

    const onPdfClick = useCallback(() => {
        track('Tickets Downloaded', {
            format: 'PDF',
            type: 'group',
            cartTicketCount: cartTicketCount
        });
        setIsPdfLoading(true);
        downloadPdfGroupTicket(cartUuid).then((result) => {
            if (result === null) {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('error.ticket-download-failed')
                });
            }
            setIsPdfLoading(false);
        });
    }, [cartUuid, track, cartTicketCount, t]);

    return (
        <div
            className={classnames(
                'rounded-6 flex flex-col lg:flex-row space-y-16 lg:space-y-0 lg:space-x-16 p-16',
                'bg-[linear-gradient(180deg,rgba(var(--primary-color-rgb),0.05)-0.14%,rgba(var(--primary-color-rgb),0.10)99.86%)]'
            )}>
            <div className="grow">
                <div className="text-body-bold-16">{t('group-ticket')}</div>
                <div className="text-body-14">{t('ticket-number', { ticketNumber: ticketId ?? '' })}</div>
            </div>
            <div className="flex flex-row space-x-16 max-w-[100%] overflow-scroll">
                <Button
                    style={ButtonStyle.surface}
                    leadingIcon={isPdfLoading ? <IconLoading16 className="animate-spin" /> : <IconDownload16 />}
                    onClick={onPdfClick}>
                    PDF
                </Button>
                {pkPass && (
                    <button
                        className="shrink-0"
                        onClick={onAppleWalletClick}>
                        {AddToAppleWallet}
                    </button>
                )}
                {googleWalletJwt && (
                    <button
                        className="shrink-0"
                        onClick={onGoogleWalletClick}>
                        {AddToGoogleWallet}
                    </button>
                )}
            </div>
        </div>
    );
};

const PaymentSucceededView: React.FC = () => {
    const { paidCartUuid } = useCartProvider();
    const t = useTranslations('tickets');
    const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
    const { track } = useAnalytics();

    const { data: ticketsWithPasses = [], isLoading: isLoadingTickets } = useQuery({
        queryKey: ['tickets-with-passes', paidCartUuid],
        // The ticket system assigns ticketSystemId a few seconds after checkout, and every download format
        // needs it. Observed round trips reach ~10s, so allow 20 (CIN-15227).
        retry(failureCount) {
            return failureCount < 20;
        },
        retryDelay: 1000,
        queryFn: async () =>
            await getAllListItems({
                queryFn: async (page: number) => {
                    const getTicketsResponse = await getTickets(paidCartUuid, {
                        page
                    });
                    const tickets = getTicketsResponse?._embedded?.tickets || [];
                    // The list answers 200 with a null ticketSystemId while the booking is still being
                    // issued, so nothing here fails on its own: throw to drive the retry above, before the
                    // wallet prefetch below asks for passes that cannot be built yet.
                    if (tickets.some((ticket) => !ticket.ticketSystemId)) {
                        throw new Error('Tickets are not issued by the ticket system yet');
                    }
                    const ticketsWithPasses = await Promise.all(
                        tickets.map(async (ticket) => {
                            let pkPass = null;
                            let googleWalletJwt = null;
                            try {
                                pkPass = await downloadAppleWalletTicket(paidCartUuid, ticket.id);
                            } catch (error) {
                                console.error(
                                    'PaymentSucceededView.tsx Error: Failed to download Apple Wallet ticket',
                                    error
                                );
                            }
                            try {
                                googleWalletJwt = await getGoogleWalletTicket(paidCartUuid, ticket.id);
                            } catch (error) {
                                console.error(
                                    'PaymentSucceededView.tsx Error: Failed to get Google Wallet ticket',
                                    error
                                );
                            }
                            return {
                                ticket,
                                pkPass,
                                googleWalletJwt
                            };
                        }) || []
                    );
                    return {
                        ...getTicketsResponse,
                        _embedded: {
                            ticketsWithPasses: ticketsWithPasses
                        }
                    };
                },
                listAccessor: 'ticketsWithPasses'
            }),
        refetchInterval: false
    });

    const { data: paidCart } = useQuery({
        queryKey: ['paid-cart', paidCartUuid],
        queryFn: async () => {
            const response = await getCartById(paidCartUuid);
            return response;
        }
    });

    return (
        <div className="flex flex-col space-y-32 max-w-[900px] mx-auto">
            <div className="flex flex-col items-center space-y-32 pb-48">
                <div className="flex flex-col items-center space-y-16">
                    <div className="w-48 h-48 flex items-center justify-center rounded-full bg-green">
                        <IconTick16 className="w-8/12 h-auto text-darkBlue" />
                    </div>
                    <h2 className="text-headline-24 text-center">{t('payment-successful')}</h2>
                    <p className="text-body-14 whitespace-pre-wrap text-center">{t('payment-successful-message')}</p>
                </div>
                <div className="w-full lg:w-2/3 h-48 flex flex-row justify-center">
                    {/* Until the ticket system has issued the tickets the download answers 409, so wait for
                        the query above rather than letting the click fail silently (CIN-15227). */}
                    <Button
                        disabled={isLoadingTickets}
                        leadingIcon={
                            isPdfLoading || isLoadingTickets ? (
                                <IconLoading16 className="animate-spin" />
                            ) : (
                                <IconDownload16 />
                            )
                        }
                        onClick={async () => {
                            setIsPdfLoading(true);
                            track('Tickets Downloaded', {
                                format: 'PDF',
                                type: 'all',
                                ticketCount: ticketsWithPasses.length
                            });
                            const result = await downloadPdfTickets(paidCartUuid);
                            if (result === null) {
                                showToast({
                                    toastType: toast.error,
                                    messageTitle: t('error.ticket-download-failed')
                                });
                            }
                            setIsPdfLoading(false);
                        }}>
                        {isLoadingTickets ? t('preparing-tickets') : t('download-tickets')}
                    </Button>
                </div>
            </div>
            <div className="flex flex-col space-y-16">
                <div className="text-center">
                    <h2 className="text-headline-24">{t('your-tickets')}</h2>
                    <p className="text-body-14 opacity-70">
                        {t('order-number', { orderNumber: paidCart?.orderId ?? '' })}
                    </p>
                </div>
                {ticketsWithPasses.length ? (
                    <>
                        {/* GROUP TICKET */}
                        {ticketsWithPasses.length > 1 && (
                            <GroupTicketRow
                                cartUuid={paidCartUuid}
                                cartTicketCount={ticketsWithPasses.length}
                            />
                        )}

                        <div className="flex flex-row opacity-50 items-center space-x-16">
                            <span className="text-body-14">{t('single-tickets')}</span>
                            <hr className="grow" />
                        </div>
                        {ticketsWithPasses.map((ticketWithPass) => (
                            <TicketRow
                                key={ticketWithPass.ticket.ticketSystemId + '-' + ticketWithPass.ticket.id}
                                ticketWithPass={ticketWithPass}
                                title={t('selected-seat.title', {
                                    row:
                                        ticketWithPass.ticket._embedded?.seat?._embedded?.seatPosition?._embedded
                                            ?.seatRow?.name ?? '',
                                    seat: ticketWithPass.ticket._embedded?.seat?._embedded?.seatPosition?.name ?? ''
                                })}
                                cartUuid={paidCartUuid}
                                cartTicketCount={ticketsWithPasses.length}
                            />
                        ))}
                    </>
                ) : isLoadingTickets ? (
                    <div className="flex items-center justify-center">
                        <ProgressBarAnimation />
                    </div>
                ) : (
                    <div>{t('no-tickets-error')}</div>
                )}
            </div>
        </div>
    );
};

export default PaymentSucceededView;
